import React, { useEffect, useState, useRef } from "react";
import styles from './subCategories.module.css';
import ProductTable from '../../components/OtherComps/ProductTable/ProductTable';
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { getProductcountThunk, getProductThunk, setProductAppliedFilters, setProductFilter, setProductPage } from "../../redux/similarProductsSlice";
import SearchProduct from "./SearchProduct";
import ProductFilter from "./ProductFilter";
import SortProduct from "./SortProduct";
import CatPagination from "../../utils/Pagination";
import { addToCartThunk } from "../../redux/cartSlice";
import { addToScheduleThunk } from "../../redux/ScheduleSlice";
import { toggleProductSelection } from "../../redux/compareSlice";

// Helper function to parse URL query params + location state
const parseURLParams = (searchString, locationState) => {
    const params = new URLSearchParams(searchString);
    const attributesMap = {};
    let isStock = false;
    let isStatus = false;

    // First populate from location.state if provided
    if (locationState?.selectedAttributes && Array.isArray(locationState.selectedAttributes)) {
        locationState.selectedAttributes.forEach(item => {
            if (item.attribute && item.values) {
                attributesMap[item.attribute] = item.values;
            }
        });
    }

    // Next parse URL query params (overriding or supplementing)
    params.forEach((value, key) => {
        if (key === 'stock') {
            isStock = value === 'true';
        } else if (key === 'status') {
            isStatus = value === 'true';
        } else if (key === 'selectedCategory' || key === 'page' || key === 'limit') {
            // metadata params ignore
        } else {
            const vals = value.split(',').map(v => v.trim()).filter(Boolean);
            if (vals.length > 0) {
                attributesMap[key] = vals;
            }
        }
    });

    const formattedFilters = Object.entries(attributesMap).map(([attribute, values]) => ({
        attribute,
        values
    }));

    return { formattedFilters, isStock, isStatus };
};

// Helper function to build URL query string from filters
const buildQueryString = (filters, isStock, isStatus, categoryId) => {
    const params = new URLSearchParams();
    if (categoryId) {
        params.set('selectedCategory', categoryId);
    }
    if (isStock) {
        params.set('stock', 'true');
    }
    if (isStatus) {
        params.set('status', 'true');
    }
    if (Array.isArray(filters)) {
        filters.forEach(item => {
            if (item.attribute && Array.isArray(item.values) && item.values.length > 0) {
                params.set(item.attribute, item.values.join(','));
            }
        });
    }
    return params.toString();
};

const SimilarProductList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { products, loading, page, productCount, search } = useSelector(state => state.similarproducts || state.products);
    const { selectedData } = useSelector(state => state.compare);

    const [quantities, setQuantities] = useState({});
    const [cartLoading, setLoadingCart] = useState({});
    const [scheduleLoading, setScheduleloading] = useState({});
    const [limit] = useState(10);
    const [isPageReady, setIsPageReady] = useState(false);

    const id = location.state?.selectedCategory || new URLSearchParams(location.search).get('selectedCategory');
    const manufacturerName = location.state?.manufacturer;

    const product = products?.data;
    const totalPages = products?.totalPages;
    const totalItems = products?.totalItems;
    const itemsPerPage = products?.limit;
    const filterData = products?.filters || [];

    // Parse initial state from URL and location.state
    const initialParsed = parseURLParams(location.search, location.state);
    const initialStateRef = useRef(initialParsed);

    const [pendingFilters, setPendingFilters] = useState(initialParsed.formattedFilters);
    const [appliedFilters_, setAppliedFilters_] = useState(initialParsed.formattedFilters);
    const [stock, setStock] = useState(initialParsed.isStock);
    const [status, setStatus] = useState(initialParsed.isStatus);

    // A previous Similar Products visit can leave Redux on a later page. Start
    // each new visit on page 1 before requesting its filters, otherwise a
    // short result set can correctly show a count but return an empty page.
    useEffect(() => {
        dispatch(setProductPage(1));
        setIsPageReady(true);
    }, [dispatch]);

    // Calculate pagination range
    const start = (page - 1) * limit + 1;
    const end = totalPages === page ? totalItems : (page - 1) * limit + limit;

    // Fetch products when applied filters, pagination, or stock/status change
    useEffect(() => {
        // A manufacturer selection has filters but no category. Allow that
        // query so it replaces results left in Redux by a previous category.
        if (!isPageReady || (!id && appliedFilters_.length === 0)) return;

        const data = {
            categoryId: id,
            page,
            limit,
            stock,
            status,
            filters: appliedFilters_
        };
        dispatch(getProductThunk(data));
    }, [dispatch, page, limit, id, appliedFilters_, stock, status, isPageReady]);

    const handleSearch = () => {
        const data = {
            categoryId: id,
            page,
            limit,
            filters: appliedFilters_,
            search
        };
        dispatch(getProductThunk(data));
    };

    const handleQuantityChange = (productId, value) => {
        setQuantities(prev => ({
            ...prev,
            [productId]: value,
        }));
    };

    const addtoCart = async (from, productId) => {
        const productsPayload = Object.entries(quantities).map(([pId, quantity]) => ({
            productId: pId,
            totalQty: parseInt(quantity, 10),
            quantity: parseInt(quantity, 10),
        }));
        if (from === 'cart') {
            setLoadingCart(prev => ({ ...prev, [productId]: true }));
            const resStatus = await dispatch(addToCartThunk(productsPayload));
            if (resStatus?.payload === 200) {
                setQuantities({});
            }
            setLoadingCart(prev => ({ ...prev, [productId]: false }));
        } else {
            setScheduleloading(prev => ({ ...prev, [productId]: true }));
            const resStatus = await dispatch(addToScheduleThunk(productsPayload));
            if (resStatus?.payload === 200) {
                setQuantities({});
            }
            setScheduleloading(prev => ({ ...prev, [productId]: false }));
        }
    };

    const handleCheckboxChange = (prod) => {
        dispatch(toggleProductSelection(prod));
    };

    // Update pendingFilters when filter option is clicked
    const handleFilterChange = (filters) => {
        setPendingFilters(filters || []);
    };

    // Keep filter counts updated
    useEffect(() => {
        if (!id && pendingFilters.length === 0) return;

        const data = {
            categoryId: id,
            filters: pendingFilters,
            stock,
            status
        };
        dispatch(getProductcountThunk(data));
    }, [dispatch, pendingFilters, id, status, stock]);

    // Apply all button handler - updates URL search params
    const handleApply = async () => {
        dispatch(setProductPage(1));
        setAppliedFilters_(pendingFilters);
        dispatch(setProductAppliedFilters(pendingFilters));

        const queryString = buildQueryString(pendingFilters, stock, status, id);
        navigate(`${location.pathname}?${queryString}`, {
            state: {
                selectedAttributes: pendingFilters,
                selectedCategory: id,
                source: location.state?.source,
                manufacturer: manufacturerName
            },
            replace: true
        });
    };

    // Sync state if URL search string or location.state changes directly
    useEffect(() => {
        const parsed = parseURLParams(location.search, location.state);
        setPendingFilters(parsed.formattedFilters);
        setAppliedFilters_(parsed.formattedFilters);
        setStock(parsed.isStock);
        setStatus(parsed.isStatus);
    }, [location.search, location.state]);

    const handleCheckboxchange = (from) => {
        if (from === 'stock') {
            setStock(!stock);
        } else {
            setStatus(!status);
        }
    };

    const handleReset = () => {
        const initial = initialStateRef.current;
        setPendingFilters(initial.formattedFilters);
        setAppliedFilters_(initial.formattedFilters);
        setStock(initial.isStock);
        setStatus(initial.isStatus);
        dispatch(setProductPage(1));
        dispatch(setProductFilter(initial.formattedFilters));
        dispatch(setProductAppliedFilters(initial.formattedFilters));
        
        const queryString = buildQueryString(initial.formattedFilters, initial.isStock, initial.isStatus, id);
        navigate(`${location.pathname}?${queryString}`, {
            state: {
                selectedAttributes: initial.formattedFilters,
                selectedCategory: id,
                source: location.state?.source,
                manufacturer: manufacturerName
            },
            replace: true
        });
    };

    return (
        <>
            <SearchProduct products={product || []} handleSearch={handleSearch} search={search} title={manufacturerName} />
            <ProductFilter
                filterData={filterData}
                onFilterChange={handleFilterChange}
                filter={pendingFilters}
                count={productCount?.data?.filteredCount}
                loading={productCount?.loading}
                handleSubmit={handleApply}
                handleCheckboxchange={handleCheckboxchange}
                stock={stock}
                status={status}
                totalItems={totalItems}
                handleReset={handleReset}
            />
            <div className={styles.sortedTable}>
                <SortProduct
                    filteredCount={productCount?.data?.filteredCount}
                    totalItems={totalItems}
                    loading={productCount?.loading}
                    filter={appliedFilters_}
                    stock={stock}
                    status={status}
                />

                {loading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px 0' }}>
                        <img src="/assets/__Iphone-spinner-1.gif" alt="Loading..." style={{ width: 35, height: 35 }} />
                    </div>
                ) : product?.length > 0 ? (
                    <>
                        <ProductTable
                            products={product}
                            quantities={quantities}
                            addtoCart={addtoCart}
                            handleQuantityChange={handleQuantityChange}
                            handleCheckboxChange={handleCheckboxChange}
                            cartLoading={cartLoading}
                            scheduleLoading={scheduleLoading}
                            selectedProducts={selectedData}
                            hide={'hide'}
                        />

                        <CatPagination
                            start={start}
                            end={end}
                            totalItems={totalItems}
                            itemsPerPage={itemsPerPage}
                            setPage={setProductPage}
                            page={page}
                        />
                    </>
                ) : (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px 0' }}>
                        No Products found
                    </div>
                )}
            </div>
        </>
    );
};

export default SimilarProductList;
